import { ref, onMounted } from 'vue'
import { io, Socket } from 'socket.io-client'
import type { DashboardData, DashboardError } from '@/plugins/api'

export type AnnotationStage = 'queued' | 'context' | 'embedding' | 'annotating' | 'done'

export interface AnnotationStatusEvent {
  taskId: string
  status: 'started' | 'progress' | 'completed' | 'failed'
  stage?: AnnotationStage
  stageStartedAt?: string // ISO timestamp when current stage started
  stageEstimatedMs?: number // Estimated duration for current stage (previous + 10%)
  model?: string
  progress?: number
  error?: string
  duration?: number
  startedAt?: string // ISO timestamp when generation started
  completedStages?: AnnotationStage[]
  estimatedDurationMs?: number
  previousDurationMs?: number
}

export interface DashboardUpdateEvent {
  type: 'refresh'
  data: DashboardData
  timestamp: string
}

export interface SystemNotificationEvent {
  type: 'info' | 'warning' | 'error' | 'success'
  title: string
  message: string
  timestamp: string
}

export interface TaskUpdateEvent {
  taskId: string
  updateType: 'history' | 'comment' | 'attachment' | 'link' | 'field' | 'all'
  triggeredBy?: string // user email or "system" or "gitlab"
  details?: Record<string, unknown>
  timestamp: string
  /** Partial task data for optimistic UI updates without API request */
  taskData?: Record<string, unknown>
}

type AnnotationStatusCallback = (event: AnnotationStatusEvent) => void
type AnnotationResetCallback = (reason: string) => void
type DashboardUpdateCallback = (data: DashboardData) => void
type DashboardErrorCallback = (error: DashboardError) => void
type SystemNotificationCallback = (notification: SystemNotificationEvent) => void
type TaskUpdateCallback = (event: TaskUpdateEvent) => void

// Activity Feed types
export interface ActivityFeedEvent {
  activity: {
    id: string
    type: string
    title: string
    body: string | null
    entityType: string | null
    entityId: string | null
    groupCount: number
    isRead: boolean
    createdAt: string
    lastEventAt: string
  }
  timestamp: string
}

export interface ActivityReadSyncEvent {
  readIds: string[]
  timestamp: string
}

type ActivityNewCallback = (event: ActivityFeedEvent) => void
type ActivityUnreadCountCallback = (count: number) => void
type ActivityReadSyncCallback = (readIds: string[]) => void

// Time Tracking Admin types
export interface TimeTrackingUpdateEvent {
  personId: string
  personName: string
  action: 'start' | 'stop' | 'update' | 'delete' | 'manual'
  entryType?: 'task' | 'break' | 'planning' | 'meeting' | 'other'
  taskId?: string
  taskTitle?: string
  taskHumanId?: string
  timestamp: string
}

type TimeTrackingUpdateCallback = (event: TimeTrackingUpdateEvent) => void

// Ideas types
export interface IdeaUpdateEvent {
  ideaId: string
  action: 'created' | 'updated' | 'deleted' | 'converted'
  triggeredBy?: string
  ideaData?: Record<string, unknown>
  timestamp: string
}

type IdeaUpdateCallback = (event: IdeaUpdateEvent) => void

// Testing Alert types (modal notification for testers)
export interface TestingAlertEvent {
  taskId: string
  taskHumanId: string
  taskTitle: string
  testerIds: string[]
  triggeredBy?: string
  timestamp: string
}

type TestingAlertCallback = (event: TestingAlertEvent) => void

// Lookups invalidation types
export interface LookupsInvalidateEvent {
  type: 'projects' | 'tasks' | 'ideas' | 'people' | 'tags' | 'articles'
  action?: 'created' | 'updated' | 'deleted'
  timestamp: string
}

type LookupsInvalidateCallback = (event: LookupsInvalidateEvent) => void

const socket = ref<Socket | null>(null)
const isConnected = ref(false)
const subscribedTasks = ref<Set<string>>(new Set())
const callbacks = ref<Map<string, Set<AnnotationStatusCallback>>>(new Map())
const globalCallbacks = ref<Set<AnnotationStatusCallback>>(new Set())
const resetCallbacks = ref<Set<AnnotationResetCallback>>(new Set())
const taskUpdateCallbacks = ref<Map<string, Set<TaskUpdateCallback>>>(new Map())
const globalTaskUpdateCallbacks = ref<Set<TaskUpdateCallback>>(new Set())
const isTasksSubscribed = ref(false)

// Ideas state
const isIdeasSubscribed = ref(false)
const ideaUpdateCallbacks = ref<Set<IdeaUpdateCallback>>(new Set())

// Dashboard state
const isDashboardSubscribed = ref(false)
const dashboardUpdateCallbacks = ref<Set<DashboardUpdateCallback>>(new Set())
const dashboardErrorCallbacks = ref<Set<DashboardErrorCallback>>(new Set())
const systemNotificationCallbacks = ref<Set<SystemNotificationCallback>>(new Set())

// Activity Feed state
const isActivitySubscribed = ref(false)
const activitySubscribedUserId = ref<string | null>(null)
const activityNewCallbacks = ref<Set<ActivityNewCallback>>(new Set())
const activityUnreadCountCallbacks = ref<Set<ActivityUnreadCountCallback>>(new Set())
const activityReadSyncCallbacks = ref<Set<ActivityReadSyncCallback>>(new Set())

// Lookups invalidation state
const isLookupsSubscribed = ref(false)
const lookupsInvalidateCallbacks = ref<Set<LookupsInvalidateCallback>>(new Set())

// Time Tracking Admin state
const isTimeTrackingAdminSubscribed = ref(false)
const timeTrackingAdminCallbacks = ref<Set<TimeTrackingUpdateCallback>>(new Set())

// Testing Alert state (modal notifications for testers)
const testingAlertCallbacks = ref<Set<TestingAlertCallback>>(new Set())

function connect() {
  if (socket.value?.connected) return

  const wsUrl = window.location.origin
  socket.value = io(`${wsUrl}/notifications`, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    autoConnect: true,
  })

  socket.value.on('connect', () => {
    console.log('[Notifications] Connected')
    isConnected.value = true

    // Re-subscribe to all tasks
    subscribedTasks.value.forEach((taskId) => {
      socket.value?.emit('subscribe:task', { taskId })
    })

    // Re-subscribe to tasks:all if there are global callbacks
    if (isTasksSubscribed.value) {
      socket.value?.emit('subscribe:tasks')
      console.log('[Notifications] Re-subscribed to all tasks updates')
    }

    // Re-subscribe to lookups invalidation
    if (isLookupsSubscribed.value) {
      socket.value?.emit('subscribe:lookups')
      console.log('[Notifications] Re-subscribed to lookups invalidation')
    }

    // Re-subscribe to activity feed
    if (isActivitySubscribed.value && activitySubscribedUserId.value) {
      socket.value?.emit('subscribe:activity', { userId: activitySubscribedUserId.value })
      console.log('[Notifications] Re-subscribed to activity for user:', activitySubscribedUserId.value)
    }
  })

  socket.value.on('disconnect', () => {
    console.log('[Notifications] Disconnected')
    isConnected.value = false
  })

  socket.value.on('annotation:status', (event: AnnotationStatusEvent) => {
    console.log('[Notifications] Annotation status:', event)

    // Call task-specific callbacks
    const taskCallbacks = callbacks.value.get(event.taskId)
    taskCallbacks?.forEach((cb) => cb(event))

    // Call global callbacks
    globalCallbacks.value.forEach((cb) => cb(event))
  })

  // Listen for reset events (when queue is stopped/cleared)
  socket.value.on('annotation:reset', (event: { type: string; reason: string; timestamp: string }) => {
    console.log('[Notifications] Annotation reset:', event.reason)
    // Call all reset callbacks
    resetCallbacks.value.forEach((cb) => cb(event.reason))
  })

  // Dashboard events
  socket.value.on('dashboard:update', (event: DashboardUpdateEvent) => {
    console.log('[Notifications] Dashboard update:', event.type)
    dashboardUpdateCallbacks.value.forEach((cb) => cb(event.data))
  })

  socket.value.on('dashboard:error', (error: DashboardError) => {
    console.log('[Notifications] Dashboard error:', error.type, error.message)
    dashboardErrorCallbacks.value.forEach((cb) => cb(error))
  })

  socket.value.on('system:notification', (notification: SystemNotificationEvent) => {
    console.log('[Notifications] System notification:', notification.type, notification.title)
    systemNotificationCallbacks.value.forEach((cb) => cb(notification))
  })

  // Task update events (history, comments, attachments, etc.)
  socket.value.on('task:update', (event: TaskUpdateEvent) => {
    console.log('[Notifications] Task update:', event.taskId, event.updateType, 'by', event.triggeredBy)
    // Call task-specific callbacks
    const taskCallbacks = taskUpdateCallbacks.value.get(event.taskId)
    taskCallbacks?.forEach((cb) => cb(event))
    // Call global task update callbacks (for task lists)
    globalTaskUpdateCallbacks.value.forEach((cb) => cb(event))
  })

  // Activity Feed events
  socket.value.on('activity:new', (event: ActivityFeedEvent) => {
    console.log('[Notifications] Activity new:', event.activity?.title)
    activityNewCallbacks.value.forEach((cb) => cb(event))
  })

  socket.value.on('activity:unread-count', (event: { count: number; timestamp: string }) => {
    console.log('[Notifications] Activity unread count:', event.count)
    activityUnreadCountCallbacks.value.forEach((cb) => cb(event.count))
  })

  socket.value.on('activity:read-sync', (event: ActivityReadSyncEvent) => {
    console.log('[Notifications] Activity read sync:', event.readIds.length, 'items')
    activityReadSyncCallbacks.value.forEach((cb) => cb(event.readIds))
  })

  // Time Tracking Admin events
  socket.value.on('time-tracking:update', (event: TimeTrackingUpdateEvent) => {
    console.log('[Notifications] Time tracking update:', event.personName, event.action, event.entryType || '')
    timeTrackingAdminCallbacks.value.forEach((cb) => cb(event))
  })

  // Ideas events
  socket.value.on('idea:update', (event: IdeaUpdateEvent) => {
    console.log('[Notifications] Idea update:', event.ideaId, event.action, 'by', event.triggeredBy)
    ideaUpdateCallbacks.value.forEach((cb) => cb(event))
  })

  // Lookups invalidation events
  socket.value.on('lookups:invalidate', (event: LookupsInvalidateEvent) => {
    console.log('[Notifications] Lookups invalidate:', event.type, event.action)
    lookupsInvalidateCallbacks.value.forEach((cb) => cb(event))
  })

  // Testing alert events (modal notification for testers when task moves to testing)
  socket.value.on('task:testing-alert', (event: TestingAlertEvent) => {
    console.log('[Notifications] Testing alert:', event.taskHumanId, event.taskTitle)
    testingAlertCallbacks.value.forEach((cb) => cb(event))
  })
}

function disconnect() {
  socket.value?.disconnect()
  socket.value = null
  isConnected.value = false
}

export function useNotifications() {
  onMounted(() => {
    connect()
  })

  // Don't disconnect on unmount - keep connection alive
  // Multiple components can share the same connection

  function subscribeToTask(taskId: string, callback?: AnnotationStatusCallback) {
    if (!subscribedTasks.value.has(taskId)) {
      subscribedTasks.value.add(taskId)
      socket.value?.emit('subscribe:task', { taskId })
    }

    if (callback) {
      if (!callbacks.value.has(taskId)) {
        callbacks.value.set(taskId, new Set())
      }
      callbacks.value.get(taskId)?.add(callback)
    }

    // Return unsubscribe function
    return () => {
      if (callback) {
        callbacks.value.get(taskId)?.delete(callback)
      }
      // Don't unsubscribe from socket if there are still callbacks
      // Or if we want to keep receiving updates for this task
    }
  }

  /**
   * Subscribe to task with confirmation - returns Promise that resolves when subscribed
   * Use this before sending requests that will emit notifications
   * IMPORTANT: Always re-subscribes to handle server restarts/pod changes
   */
  function subscribeToTaskAsync(taskId: string, callback?: AnnotationStatusCallback): Promise<boolean> {
    return new Promise((resolve) => {
      // Add callback first (even if we're already "subscribed" client-side)
      if (callback) {
        if (!callbacks.value.has(taskId)) {
          callbacks.value.set(taskId, new Set())
        }
        callbacks.value.get(taskId)?.add(callback)
      }

      // Check socket connection
      if (!socket.value?.connected) {
        console.warn('[Notifications] Socket not connected, attempting to connect...')
        connect()
        // Wait a bit for connection
        setTimeout(() => {
          if (socket.value?.connected) {
            doSubscribe()
          } else {
            console.error('[Notifications] Failed to connect socket')
            resolve(false)
          }
        }, 1000)
        return
      }

      doSubscribe()

      function doSubscribe() {
        // ALWAYS emit subscribe:task to server, even if we think we're subscribed
        // This handles server pod restarts where the room membership is lost
        socket.value?.emit('subscribe:task', { taskId }, (response: { success: boolean; room?: string }) => {
          if (response?.success) {
            subscribedTasks.value.add(taskId)
            console.log(`[Notifications] Subscribed to task ${taskId} (confirmed)`)
            resolve(true)
          } else {
            console.error(`[Notifications] Failed to subscribe to task ${taskId}`)
            resolve(false)
          }
        })

        // Timeout fallback in case server doesn't respond
        setTimeout(() => {
          // Resolve true anyway as fallback - the subscription was sent
          if (!subscribedTasks.value.has(taskId)) {
            console.warn(`[Notifications] Subscription timeout for task ${taskId}, assuming success`)
            subscribedTasks.value.add(taskId)
          }
          resolve(true)
        }, 3000)
      }
    })
  }

  function unsubscribeFromTask(taskId: string) {
    subscribedTasks.value.delete(taskId)
    callbacks.value.delete(taskId)
    taskUpdateCallbacks.value.delete(taskId)
    socket.value?.emit('unsubscribe:task', { taskId })
  }

  function subscribeToAll(callback: AnnotationStatusCallback) {
    globalCallbacks.value.add(callback)
    socket.value?.emit('subscribe:all')

    return () => {
      globalCallbacks.value.delete(callback)
    }
  }

  function onAnnotationReset(callback: AnnotationResetCallback) {
    resetCallbacks.value.add(callback)
    return () => {
      resetCallbacks.value.delete(callback)
    }
  }

  // Dashboard subscription
  function subscribeToDashboard(onUpdate: DashboardUpdateCallback, onError?: DashboardErrorCallback) {
    // Add callbacks
    dashboardUpdateCallbacks.value.add(onUpdate)
    if (onError) {
      dashboardErrorCallbacks.value.add(onError)
    }

    // Subscribe to dashboard updates if not already
    if (!isDashboardSubscribed.value) {
      socket.value?.emit('subscribe:dashboard')
      isDashboardSubscribed.value = true
    }

    // Return unsubscribe function
    return () => {
      dashboardUpdateCallbacks.value.delete(onUpdate)
      if (onError) {
        dashboardErrorCallbacks.value.delete(onError)
      }

      // Unsubscribe if no more callbacks
      if (dashboardUpdateCallbacks.value.size === 0) {
        socket.value?.emit('unsubscribe:dashboard')
        isDashboardSubscribed.value = false
      }
    }
  }

  // System notifications
  function onSystemNotification(callback: SystemNotificationCallback) {
    systemNotificationCallbacks.value.add(callback)
    return () => {
      systemNotificationCallbacks.value.delete(callback)
    }
  }

  /**
   * Subscribe to task update events (history, comments, attachments, etc.)
   * The task must already be subscribed via subscribeToTask or subscribeToTaskAsync
   */
  function onTaskUpdate(taskId: string, callback: TaskUpdateCallback) {
    // Ensure we're subscribed to the task room
    if (!subscribedTasks.value.has(taskId)) {
      subscribeToTask(taskId)
    }

    // Add callback
    if (!taskUpdateCallbacks.value.has(taskId)) {
      taskUpdateCallbacks.value.set(taskId, new Set())
    }
    taskUpdateCallbacks.value.get(taskId)?.add(callback)

    // Return unsubscribe function
    return () => {
      taskUpdateCallbacks.value.get(taskId)?.delete(callback)
    }
  }

  /**
   * Subscribe to all task updates (for task lists, time tracking, etc.)
   * Receives updates for ANY task field change
   */
  function subscribeToAllTasks(callback: TaskUpdateCallback) {
    globalTaskUpdateCallbacks.value.add(callback)

    // Subscribe to tasks:all room if not already
    if (!isTasksSubscribed.value) {
      socket.value?.emit('subscribe:tasks')
      isTasksSubscribed.value = true
      console.log('[Notifications] Subscribed to all tasks updates')
    }

    // Return unsubscribe function
    return () => {
      globalTaskUpdateCallbacks.value.delete(callback)

      // Unsubscribe if no more callbacks
      if (globalTaskUpdateCallbacks.value.size === 0) {
        socket.value?.emit('unsubscribe:tasks')
        isTasksSubscribed.value = false
      }
    }
  }

  /**
   * Subscribe to activity feed for a specific user
   * Must pass the user's personId (not Keycloak ID)
   */
  function subscribeToActivity(
    userId: string,
    onNew: ActivityNewCallback,
    onUnreadCount?: ActivityUnreadCountCallback,
    onReadSync?: ActivityReadSyncCallback
  ) {
    // Add callbacks
    activityNewCallbacks.value.add(onNew)
    if (onUnreadCount) {
      activityUnreadCountCallbacks.value.add(onUnreadCount)
    }
    if (onReadSync) {
      activityReadSyncCallbacks.value.add(onReadSync)
    }

    // Subscribe if not already for this user
    if (!isActivitySubscribed.value || activitySubscribedUserId.value !== userId) {
      // Unsubscribe from previous user if any
      if (activitySubscribedUserId.value) {
        socket.value?.emit('unsubscribe:activity', { userId: activitySubscribedUserId.value })
      }

      socket.value?.emit('subscribe:activity', { userId })
      isActivitySubscribed.value = true
      activitySubscribedUserId.value = userId
      console.log('[Notifications] Subscribed to activity for user:', userId)
    }

    // Return unsubscribe function
    return () => {
      activityNewCallbacks.value.delete(onNew)
      if (onUnreadCount) {
        activityUnreadCountCallbacks.value.delete(onUnreadCount)
      }
      if (onReadSync) {
        activityReadSyncCallbacks.value.delete(onReadSync)
      }

      // Unsubscribe if no more callbacks
      if (activityNewCallbacks.value.size === 0) {
        socket.value?.emit('unsubscribe:activity', { userId })
        isActivitySubscribed.value = false
        activitySubscribedUserId.value = null
      }
    }
  }

  /**
   * Subscribe to time tracking admin updates
   * Used by TimeTrackingAdminView to get real-time updates when employees change their tracking
   */
  function subscribeToTimeTrackingAdmin(callback: TimeTrackingUpdateCallback) {
    timeTrackingAdminCallbacks.value.add(callback)

    // Subscribe if not already
    if (!isTimeTrackingAdminSubscribed.value) {
      socket.value?.emit('subscribe:time-tracking-admin')
      isTimeTrackingAdminSubscribed.value = true
      console.log('[Notifications] Subscribed to time-tracking admin updates')
    }

    // Return unsubscribe function
    return () => {
      timeTrackingAdminCallbacks.value.delete(callback)

      // Unsubscribe if no more callbacks
      if (timeTrackingAdminCallbacks.value.size === 0) {
        socket.value?.emit('unsubscribe:time-tracking-admin')
        isTimeTrackingAdminSubscribed.value = false
        console.log('[Notifications] Unsubscribed from time-tracking admin updates')
      }
    }
  }

  /**
   * Subscribe to all ideas updates (for ideas list)
   * Receives updates for ANY idea creation/update/deletion
   */
  function subscribeToAllIdeas(callback: IdeaUpdateCallback) {
    ideaUpdateCallbacks.value.add(callback)

    // Subscribe to ideas:all room if not already
    if (!isIdeasSubscribed.value) {
      socket.value?.emit('subscribe:ideas')
      isIdeasSubscribed.value = true
      console.log('[Notifications] Subscribed to all ideas updates')
    }

    // Return unsubscribe function
    return () => {
      ideaUpdateCallbacks.value.delete(callback)

      // Unsubscribe if no more callbacks
      if (ideaUpdateCallbacks.value.size === 0) {
        socket.value?.emit('unsubscribe:ideas')
        isIdeasSubscribed.value = false
        console.log('[Notifications] Unsubscribed from all ideas updates')
      }
    }
  }

  /**
   * Subscribe to testing alert events (modal notification for testers)
   * Shows a blocking modal when a task moves to "testing" status
   * The callback receives the event and should check if current user is in testerIds
   */
  function onTestingAlert(callback: TestingAlertCallback) {
    testingAlertCallbacks.value.add(callback)
    return () => {
      testingAlertCallbacks.value.delete(callback)
    }
  }

  /**
   * Subscribe to lookups invalidation events (projects, people, tags)
   * Used by useLookups composable to keep selector lists in sync
   */
  function subscribeToLookups(callback: LookupsInvalidateCallback) {
    lookupsInvalidateCallbacks.value.add(callback)

    if (!isLookupsSubscribed.value) {
      socket.value?.emit('subscribe:lookups')
      isLookupsSubscribed.value = true
      console.log('[Notifications] Subscribed to lookups invalidation')
    }

    return () => {
      lookupsInvalidateCallbacks.value.delete(callback)

      if (lookupsInvalidateCallbacks.value.size === 0) {
        socket.value?.emit('unsubscribe:lookups')
        isLookupsSubscribed.value = false
        console.log('[Notifications] Unsubscribed from lookups invalidation')
      }
    }
  }

  /**
   * Subscribe to activity read sync events
   * Used to sync read status across multiple devices
   */
  function onActivityReadSync(callback: ActivityReadSyncCallback) {
    activityReadSyncCallbacks.value.add(callback)
    return () => {
      activityReadSyncCallbacks.value.delete(callback)
    }
  }

  return {
    socket,
    isConnected,
    subscribeToTask,
    subscribeToTaskAsync,
    unsubscribeFromTask,
    subscribeToAll,
    onAnnotationReset,
    subscribeToDashboard,
    onSystemNotification,
    onTaskUpdate,
    subscribeToAllTasks,
    subscribeToActivity,
    subscribeToTimeTrackingAdmin,
    subscribeToAllIdeas,
    subscribeToLookups,
    onTestingAlert,
    onActivityReadSync,
    disconnect,
  }
}

// Auto-connect on module load
if (typeof window !== 'undefined') {
  connect()
}
